import type { SyncNotice } from '@infra/sync/hybrid/HybridSyncEngine';
import { ToastService } from '@presentation/components/Toast';

/** Un même type de notification n'est pas affiché plus d'une fois par seconde. */
export const NOTICE_MIN_GAP_MS = 1000;

export interface InitialSummary {
  downloaded: number;
  uploaded: number;
  replaced: number;
  identical: number;
  at: string;
}

const lastShownAt = new Map<SyncNotice['kind'], number>();
let lastSummary: InitialSummary | null = null;
const summaryListeners = new Set<(summary: InitialSummary | null) => void>();

function setSummary(next: InitialSummary | null): void {
  lastSummary = next;
  for (const listener of [...summaryListeners]) {
    try {
      listener(next);
    } catch {
      /* un abonné défaillant ne bloque pas les autres */
    }
  }
}

/** Récapitulatif du dernier premier envoi (affiché aussi dans le panneau « Synchronisation »). */
export function getLastInitialSummary(): InitialSummary | null {
  return lastSummary;
}

export function subscribeInitialSummary(listener: (summary: InitialSummary | null) => void): () => void {
  summaryListeners.add(listener);
  return () => summaryListeners.delete(listener);
}

/** Oublie le récapitulatif (changement de société ou de session). */
export function clearInitialSummary(): void {
  if (lastSummary) setSummary(null);
}

/** Réinitialise l'anti-répétition (tests). */
export function resetHybridSyncNotices(): void {
  lastShownAt.clear();
  setSummary(null);
}

export function initialSummaryText(summary: Pick<InitialSummary, 'downloaded' | 'uploaded' | 'replaced'>): string {
  const received = `${summary.downloaded} fiche${summary.downloaded > 1 ? 's' : ''} reçue${summary.downloaded > 1 ? 's' : ''}`;
  const sent = `${summary.uploaded} envoyée${summary.uploaded > 1 ? 's' : ''}`;
  const aside =
    summary.replaced > 0
      ? `, ${summary.replaced} copie${summary.replaced > 1 ? 's' : ''} locale${summary.replaced > 1 ? 's' : ''} mise${summary.replaced > 1 ? 's' : ''} de côté dans « À examiner »`
      : '';
  return `Première synchronisation terminée : ${received}, ${sent}${aside}.`;
}

/** Notifications discrètes de la synchronisation (jamais de boîte de dialogue bloquante). */
export function notifyHybridSyncNotice(notice: SyncNotice): void {
  // Le récapitulatif est toujours conservé pour le panneau, même si le message est retenu par l'anti-répétition.
  if (notice.kind === 'initial_summary') {
    setSummary({
      downloaded: notice.downloaded,
      uploaded: notice.uploaded,
      replaced: notice.replaced,
      identical: notice.identical,
      at: new Date().toISOString(),
    });
  }
  const now = Date.now();
  const previous = lastShownAt.get(notice.kind);
  if (previous !== undefined && now - previous < NOTICE_MIN_GAP_MS) return;
  lastShownAt.set(notice.kind, now);

  switch (notice.kind) {
    case 'merged_from_other_device':
      ToastService.info(
        notice.count === 1
          ? 'Une fiche a été mise à jour avec des modifications venues d’un autre poste.'
          : `${notice.count} fiches ont été mises à jour avec des modifications venues d’un autre poste.`,
      );
      break;
    case 'bulletin_locked':
      ToastService.warning('Bulletin verrouillé : votre modification n’a pas été appliquée. Elle est gardée dans « À examiner ».');
      break;
    case 'entry_rejected':
      ToastService.warning(`Modification refusée par le serveur : ${notice.message} Elle est gardée dans « À examiner ».`);
      break;
    case 'initial_summary':
      ToastService.info(initialSummaryText(notice));
      break;
    case 'rebootstrap':
      ToastService.info(
        notice.reason === 'epoch_changed'
          ? 'Le serveur a été restauré : votre poste vérifie et renvoie ses données. Vous pouvez continuer à travailler.'
          : 'Ce poste était absent depuis longtemps : une synchronisation complète est en cours. Vous pouvez continuer à travailler.',
      );
      break;
    default:
      break;
  }
}
